/* jshint esversion: 6 */
import React from 'react';
import { SUB_PREVIEW_UPDATE } from 'app/redux/constants.js';
import PlanChangeDialog from '@/shadcn/components/account/PlanChange/PlanChangeDialog.tsx';
import { listPrice, upgradeFeatures } from '@/shadcn/components/account/PlanChange/plans.ts';
import useChangePreview, { useConfirmationStep } from './useChangePreview.js';

/*
 * Opens through the legacy modal stack to confirm moving to a bigger plan. Three cases:
 * - restarting a canceled plan on the same level, which costs nothing today;
 * - switching a trial to a bigger plan's trial, billed when the trial ends;
 * - an upgrade, prorated today.
 *
 * Subscription.updatePlan passes the target `level` / `term`, the social packs that carry
 * over (`adjustedAddOns`) and the target's limits (`newLimits`), plus what confirming and
 * cancelling do. The page closes the modal once SUB_UPDATE settles.
 */
export default function PlanChangeConfirm(props) {
    const { app, plans, user, level, term, adjustedAddOns, newLimits } = props,
        constants = app.config.constants,
        current = plans.current,
        currentLevel = parseInt(current.planLevel, 10),
        nextTerm = parseInt(term, 10),
        restart = user.detail.subscriptionState == constants.SUBSCRIPTION_CANCELLED && level === currentLevel,
        trial = !restart && current.trialEndsAt > 0 && current.trialEndsAt * 1000 > Date.now(),
        kind = restart ? 'restart' : (trial ? 'trial' : 'upgrade'),
        post = { level, term: nextTerm },
        fromDetail = plans.details[currentLevel] || {},
        toDetail = plans.details[level] || {},
        currentLimits = props.calculateAddOns();

    if (adjustedAddOns) {
        post.add_on_social = adjustedAddOns;
    }

    const preview = useChangePreview(props, SUB_PREVIEW_UPDATE, post, kind !== 'restart');

    useConfirmationStep();

    const close = () => {
        if (props.onCancel) {
            props.onCancel();
        }
        props.closeAllModals();
    };

    // A trial bills monthly once it ends, at the plan's own price.
    const afterTrialPrice = listPrice(toDetail, constants.SUBSCRIPTION_TERM_MONTHLY, { add_on_social: adjustedAddOns });

    return (
        <PlanChangeDialog
            kind={kind}
            from={{
                label: fromDetail.label,
                outputs: currentLimits.outputs,
                inputs: currentLimits.inputs,
                postLimit: parseInt(user.perms.perms.postLimit, 10) || 0,
                price: Number(currentLimits.price)
            }}
            to={{
                label: toDetail.label,
                outputs: newLimits.outputs,
                inputs: newLimits.inputs,
                postLimit: parseInt(toDetail.limits?.postLimit, 10) || 0,
                price: kind === 'trial' ? afterTrialPrice : Number(kind === 'restart' ? currentLimits.price : newLimits.price)
            }}
            annual={nextTerm === constants.SUBSCRIPTION_TERM_ANNUAL}
            features={upgradeFeatures(constants, level)}
            trialEnds={trial ? current.trialEndsAt * 1000 : null}
            nextBilling={current.nextBilling}
            preview={preview}
            busy={app.loading.SUB_UPDATE}
            onConfirm={() => {
                let data = {
                    level,
                    term: nextTerm,
                    trackingAction: 'planupgrade'
                };
                if (adjustedAddOns) {
                    data.add_on_social = adjustedAddOns;
                }
                props.onConfirm(data);
            }}
            onCancel={close}
            onClose={close} />
    );
}
